/**
 * Non-React ECS queries that operate on the store directly.
 *
 * These are used in store actions and event handlers where React hooks
 * are unavailable. They call buildRegistry on demand with current state.
 */

import { useStore } from "../app/store.ts";
import { buildRegistry, type RegistryInput } from "./buildRegistry.ts";
import type { TransformComponent } from "./components.ts";
import type { Entity, EntityRegistry } from "./Entity.ts";
import { storeKeyToEntityId } from "./EntityId.ts";

/** Build a registry from current store state. */
export function getRegistry(): EntityRegistry {
  const s = useStore.getState();
  return buildRegistry({
    blendFiles: s.blendFiles,
    sceneObjects: s.sceneObjects,
    collections: s.collections,
    materials: s.materials,
    visibility: s.visibility,
    modifierOverrides: s.modifierOverrides,
    shapeKeyWeights: s.shapeKeyWeights,
    mannequinMorphs: s.mannequinMorphs,
    mannequinBonePoses: s.mannequinBonePoses,
  });
}

/** Build a registry from explicit input (e.g. from a snapshot). */
export function getRegistryFrom(input: RegistryInput): EntityRegistry {
  return buildRegistry(input);
}

/**
 * Resolve selected entities from the current store.
 * Returns entities with their transforms for the given selection set.
 */
export function getSelectedTransforms(
  selectedObjects: ReadonlySet<string>,
  registry: EntityRegistry,
): Array<{ entity: Entity; transform: TransformComponent }> {
  const result: Array<{ entity: Entity; transform: TransformComponent }> = [];
  for (const key of selectedObjects) {
    const entity = registry.get(storeKeyToEntityId(key));
    if (!entity) continue;
    const transform = entity.components.transform;
    if (!transform) continue;
    result.push({ entity, transform });
  }
  return result;
}

/**
 * Compute the centroid of selected objects' positions.
 * Returns null if no selected objects have transforms.
 */
export function computeSelectedCentroid(
  selectedObjects: ReadonlySet<string>,
  registry: EntityRegistry,
): [number, number, number] | null {
  let cx = 0,
    cy = 0,
    cz = 0,
    count = 0;
  for (const key of selectedObjects) {
    const entity = registry.get(storeKeyToEntityId(key));
    const t = entity?.components.transform;
    if (!t) continue;
    cx += t.position[0];
    cy += t.position[1];
    cz += t.position[2];
    count++;
  }
  if (count === 0) return null;
  return [cx / count, cy / count, cz / count];
}
